import { css } from '@emotion/react';
import { useExitLecturePageMode, useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import React from 'react';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/schemas';
import { updateLectureComponentOrder } from 'redux/actions/lecture-components';
import { setLecturePageModeIsSwitching } from 'redux/actions/lecture-pages';
import { getCurrentCourse } from 'redux/selectors/course';

import { black, hexToRgbaString, warning, white } from 'styles/global_defaults/colors';
import { doubleSpacing, lectureTopPanelZIndex } from 'styles/global_defaults/scaffolding';

import { Button } from 'react-bootstrap';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { LecturePageMode } from '.';
import { EditMenuRightPanel } from './edit-menu/edit-menu-right-panel';
import ReorderButton from './edit-menu/actions/reorder-button';
import EditModeCenterButton from './edit-menu/actions/edit-mode-center-button';
import ExitButton from './edit-menu/actions/exit-button';
import AiSuggestActivityButton from './edit-menu/actions/ai-suggest-activity-button';
import UpdateColor from './update-course-colors/update-colors';

import { config } from '../../../config/config.json';

/** The transparent black bar panel that shows buttons for saving changes and exiting the Reorder and
 * Exit lecture page modes. Does not display in View mode. */
export const LecturePageTopPanel = () => {
  const styles = css`
    z-index: ${lectureTopPanelZIndex};
    position: absolute;
    width: 100%;
    height: 60px;
    top: -60px;
    display: flex;
    align-items: center;

    &.lesson-library-top-panel {
      justify-content: center;
      background: ${hexToRgbaString('#000000', 0.5)};
    }

    &.course-level-top-panel {
      background: ${hexToRgbaString(black)};
      box-shadow: 0px 5px 10px 0px ${hexToRgbaString(black, 0.2)};
      justify-content: space-between;
    }

    &.restricted-mode-panel {
     justify-content: end;
    }

    .reorder-close-icon {
      border: 2px solid ${warning};

      &:hover {
        background-color: ${hexToRgbaString(white, 0.2)} !important;
      }
    }

    .edit-mode-buttons {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }

    .edit-menu-icon-styles {
      width: ${doubleSpacing}px;
      height: ${doubleSpacing}px;
      border-radius: 50%;
      background-color: ${hexToRgbaString(white, 0.2)};

      &:hover {
        background-color: ${hexToRgbaString(white, 0.4)};
        cursor: pointer;
      }
    }
  `;

  const params = useLecturePageParams();

  const dispatch = useAppDispatch();
  const lecturePage = useSelector(state => state.models.lecturePages[params.lecturePageId]);
  const preventPageNavigation = useSelector((state: RootState) => state.app.lecturePage.preventPageNavigation);
  const isModeSwitching = useSelector((state: RootState) => state.app.lecturePage.isModeSwitching);

  const { isContentManagementCollection } = useSelector(getCurrentCourse);

  const restrictedMode = params.mode === LecturePageMode.RESTRICTED_EDIT;
  const exitMode = useExitLecturePageMode();

  // TODO: Should this logic be added to the exitMode hook directly?
  const doExitMode = async (saveReorder: boolean = false) => {
    if (params.mode === LecturePageMode.REORDER) {
      if (saveReorder) {
        // hideNewComponentAddUI(dispatch);
        // Save component reorderings if necessary
        return dispatch(updateLectureComponentOrder({
          catalogId: params.catalogId,
          lecturePageId: lecturePage.id,
          lectureComponents: lecturePage.lectureComponents,
        })).then(() => exitMode());
      }

      // hideNewComponentAddUI(dispatch);
      exitMode();
      dispatch(setLecturePageModeIsSwitching(false));
      return null;
    }

    // hideNewComponentAddUI(dispatch);
    exitMode();

    return null;
  };

  const exitToPreviousMode = (saveReorder: boolean = false) => {
    dispatch(setLecturePageModeIsSwitching(true));
    doExitMode(saveReorder).then(() => {
      dispatch(setLecturePageModeIsSwitching(false));
    });
  };

  return (
    <div css={styles} className={`${isContentManagementCollection ? 'lesson-library-top-panel' : 'course-level-top-panel'} ${restrictedMode ? 'restricted-mode-panel' : ''}`}>
      {(params.mode === LecturePageMode.EDIT
        || params.mode === LecturePageMode.LINKED_EDIT) && (
          <React.Fragment>
            <div className='d-flex'>
              <AiSuggestActivityButton />
              <ReorderButton />
              <UpdateColor />
            </div>
            <div className='edit-mode-buttons align-items-center'>
              <EditModeCenterButton exitToPreviousMode={exitToPreviousMode} />
            </div>
            <EditMenuRightPanel onExitClick={() => exitToPreviousMode()} />
          </React.Fragment>
      )}
      {(params.mode === LecturePageMode.RESTRICTED_EDIT) && (
        <React.Fragment>
          <div className='edit-mode-buttons'>
            <EditModeCenterButton exitToPreviousMode={exitToPreviousMode} />
          </div>
          <ExitButton onClick={() => exitToPreviousMode()} />
        </React.Fragment>
      )}

      {params.mode === LecturePageMode.REORDER && (
        <React.Fragment>
          {isContentManagementCollection
            ? (
              <Button
                variant='secondary'
                className='bs4-night mr-2'
                onClick={() => exitToPreviousMode(false)}
                data-qa={config.pendo.lectureEdit.editMenuTopPanel.cancelReorderModeButton}
              >
                {t.FORM.CANCEL()}
              </Button>
            )
            : (
              <ClickableContainer
                className='edit-menu-icon-styles reorder-close-icon align-items-center d-inline-flex justify-content-center mx-2'
                onClick={() => exitToPreviousMode(false)}
                data-qa={config.pendo.lectureEdit.editMenuTopPanel.cancelReorderModeButton}
              >
                <NvIcon icon='close' size='xs-smallest' className='text-white' />
              </ClickableContainer>
            )}
          <Button
            variant='primary'
            disabled={!preventPageNavigation || isModeSwitching}
            onClick={() => exitToPreviousMode(true)}
            data-qa='exit-reorder-mode-button'
            className={isContentManagementCollection ? '' : 'edit-mode-buttons text-small font-weight-bold py-1 px-2'}
          >
            {t.LECTURE_PAGES.ADMIN.TOP_HEADER.EXIT_REORDER()}
          </Button>
        </React.Fragment>
      )}
    </div>
  );
};

export default LecturePageTopPanel;
